/**
 * ObservationTaskPanelV2a — Variant panel for the "deferred criterion mapping" workflow.
 *
 * Key differences from the standard ObservationTaskPanel:
 *
 *   1. A single "master" StructuredNotesEditor sits below Task Administration.
 *      The clinician captures all observations here without needing to decide
 *      which DSM criterion each note belongs to during the live appointment.
 *
 *   2. The DSM Criteria Contribution table is hidden by default. It only appears
 *      when the clinician activates "Criterion Mapping" mode via the sidebar
 *      toggle (prop: `criterionMappingMode`).
 *
 *   3. When Criterion Mapping mode is ON, notes from the master box can be
 *      dragged and dropped into the appropriate DSM criterion row. Notes moved
 *      to a criterion row are removed from the master list, keeping a clear
 *      "inbox zero" workflow.
 *
 * This variant supports the clinical reality that initial observations are often
 * recorded as a stream of consciousness, with criterion attribution being a
 * post-session reflective exercise.
 */

import React, { useState, useCallback } from 'react';
import { ChevronDownIcon } from '@heroicons/react/solid';
import InfoTooltip from '../../../../components/InfoTooltip';
import { DSM5_DOMAINS, DSM5_EVIDENCE_OPTIONS } from '../config/observationTasksConfig';
import StructuredNotesEditor, { PROBING_Q_MIME } from './StructuredNotesEditor';
import { moveNoteBetweenArrays } from '../../../../utils/structuredNotesMove';
import { AUTISM_EVIDENCE_BUTTON_COLOURS } from '../../../../constants/autismEvidenceColors';
import { handleExternalResourceClick } from '../utils/openExternalResourceWindow';

/** Evidence buttons — Clear = red, No evidence = green (shared Autism scale). */
const EVIDENCE_BUTTON_COLOURS = AUTISM_EVIDENCE_BUTTON_COLOURS;

export default function ObservationTaskPanelV2a({
  task,
  taskData,
  onFieldChange,
  selectedTaskIds = [],
  showObservations = false,
  criterionMappingMode = false,
  sideBySideLayout = false,
  moveTargets = null,
  onMoveNote = null,
  readOnly: readOnlyProp = false,
  // When a function, renders an interactive "Not administered" switch in the
  // header (Task Administration). When null, the flag is shown read-only
  // (Criterion Mapping). The flag itself is read from `taskData.notAdministered`.
  onToggleNotAdministered = null,
  // v2-only: enables the master-note Tag picker (tag-to-criterion). Defaults
  // false so v1 patients — and the legacy Criterion Mapping which reuses this
  // panel — never surface tags they cannot act on.
  taggingEnabled = false,
}) {
  const [removingCriterionId, setRemovingCriterionId] = useState(null);
  const [addSymptomPickerOpen, setAddSymptomPickerOpen] = useState(false);
  const [sectionBOpen, setSectionBOpen] = useState(false);

  // A task marked "Not administered" keeps its notes visible but locks all
  // content editing, exactly like the existing preview/unassigned read-only
  // path. Deriving `readOnly` here means every existing content-editing guard
  // in this component (questionnaire, master notes, criterion table, add/remove
  // criteria) inherits the lock with no further changes.
  const notAdministered = Boolean(taskData?.notAdministered);
  const readOnly = readOnlyProp || notAdministered;

  // The switch that toggles the flag is interactive only when the caller wired
  // a handler AND the page itself is editable (not preview/unassigned).
  const canToggleNotAdministered = typeof onToggleNotAdministered === 'function' && !readOnlyProp;

  const currentValue = (fieldId) => taskData?.[fieldId] ?? null;

  /**
   * Moves a note between criterion rows, or from the master box into a
   * criterion row. When sourceDomainId is "master", we pull from the
   * masterNotes array instead of a dsm_X_notes key.
   */
  const handleMoveNoteBetweenCriteria = useCallback((sourceDomainId, noteId, targetDomainId, insertAt) => {
    const sourceKey = sourceDomainId === 'master' ? 'masterNotes' : `dsm_${sourceDomainId}_notes`;
    const targetKey = `dsm_${targetDomainId}_notes`;
    const sourceNotes = Array.isArray(taskData?.[sourceKey]) ? taskData[sourceKey] : [];
    const targetNotes = Array.isArray(taskData?.[targetKey]) ? taskData[targetKey] : [];

    const movedNote = sourceNotes.find(n => n.id === noteId);
    if (!movedNote) return;

    if (sourceDomainId === 'master') {
      // Copy FROM master: the note stays in master (can be mapped to multiple
      // criteria), so this is a copy — not a move. Prevent duplicates in the
      // target. Kept inline because moveNoteBetweenArrays is move-only.
      const alreadyInTarget = targetNotes.some(n => n.id === noteId);
      if (alreadyInTarget) return;
      const updated = [...targetNotes];
      if (insertAt != null && insertAt <= updated.length) {
        updated.splice(insertAt, 0, { ...movedNote });
      } else {
        updated.push({ ...movedNote });
      }
      onFieldChange(targetKey, updated);
    } else {
      // Moving between criterion boxes — remove from source, add to target.
      const result = moveNoteBetweenArrays(sourceNotes, targetNotes, noteId, insertAt);
      if (!result) return;
      onFieldChange(sourceKey, result.source);
      onFieldChange(targetKey, result.target);
    }
  }, [taskData, onFieldChange]);

  /**
   * Derives the set of master note IDs that have been copied into at least one
   * criterion's notes array. Used to render a "mapped" visual indicator.
   */
  const mappedNoteIds = React.useMemo(() => {
    const ids = new Set();
    const relevantCriteria = task.relevantCriteria || [];
    const extraCriteria = taskData?.dsm_extraCriteria || [];
    const allCriteria = [...relevantCriteria, ...extraCriteria];

    for (const cId of allCriteria) {
      const criterionNotes = taskData?.[`dsm_${cId}_notes`];
      if (Array.isArray(criterionNotes)) {
        for (const n of criterionNotes) {
          ids.add(n.id);
        }
      }
    }
    return ids;
  }, [task.relevantCriteria, taskData]);

  // -------------------------------------------------------------------
  // Section renderers
  // -------------------------------------------------------------------

  const renderSectionHeading = (title, tooltip) => (
    <div className="flex items-center gap-2 mb-3">
      <h2 className="text-gray-900 text-base font-semibold text-left">{title}</h2>
      {tooltip && (
        <InfoTooltip content={tooltip} label={`${title} info`} maxWidth="320px" />
      )}
    </div>
  );

  /**
   * Renders the questionnaire questions in Task Administration with
   * click-to-toggle "Marked as asked" functionality.
   */
  const renderQuestionnaireSection = () => {
    const section = task.sections.find(s => s.id === 'A');
    if (!section) return null;

    const questionnaireFields = (section.fields || []).filter(f => f.type === 'questionnaire');
    const hasQuestions = questionnaireFields.length > 0;

    return (
      <div className="mb-6">
        {renderSectionHeading(section.title)}
        <div className="px-5 py-4 bg-white rounded-xl border border-gray-100">
          {/* Stimulus cards — unmodified click opens a new *window* (so the
              OBS module stays visible during the appointment). Cmd/ctrl-click
              still falls through to target="_blank" for a new tab. */}
          {task.externalResources?.length > 0 && (
            <div className="flex gap-3 mb-4 flex-wrap">
              {task.externalResources.map((res, idx) => (
                <a
                  key={idx}
                  href={res.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(event) => handleExternalResourceClick(event, res.url)}
                  className="group flex flex-col items-center w-32 rounded-xl
                    border border-gray-200 bg-gray-50
                    hover:border-primary/40 hover:shadow-md
                    transition-all duration-200 overflow-hidden"
                >
                  <div className="w-full h-20 overflow-hidden bg-gray-100">
                    <img
                      src={res.thumbnail}
                      alt={res.label}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 px-2 py-2 w-full">
                    <span className="text-xs font-semibold text-gray-700 group-hover:text-primary truncate transition-colors">
                      {res.label}
                    </span>
                    <svg className="w-3 h-3 flex-shrink-0 text-gray-300 group-hover:text-primary/60 transition-colors ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                  </div>
                </a>
              ))}
            </div>
          )}

          {hasQuestions ? (
            // Top-level topic groups (Money, Living arrangements, …) get a
            // generous gap so subheadings are not flush against the previous
            // set. Nested branches (field.nested) sit tighter under their
            // parent as quieter sub-subheadings.
            <div className="flex flex-col">
            {questionnaireFields.map((field, fieldIndex) => {
              const asked = currentValue(field.id) || [];
              const askedSet = new Set(asked);
              const isNested = Boolean(field.nested);

              const toggleAsked = (idx) => {
                if (readOnly) return;
                const next = askedSet.has(idx)
                  ? asked.filter(i => i !== idx)
                  : [...asked, idx];
                onFieldChange(field.id, next);
              };

              return (
                <div
                  key={field.id}
                  className={fieldIndex === 0 ? '' : isNested ? 'mt-2.5' : 'mt-4'}
                >
                  <label
                    className={`block text-left ${
                      isNested
                        ? 'text-slate-500 text-xs font-medium mb-1.5 pl-0.5'
                        : 'text-slate-700 text-sm font-semibold mb-2'
                    }`}
                  >
                    {typeof field.label === 'string' && field.label.includes('__')
                      ? field.label.split(/(__[^_]+__)/).map((part, i) =>
                          part.startsWith('__') && part.endsWith('__')
                            ? <u key={i}>{part.slice(2, -2)}</u>
                            : part
                        )
                      : field.label}
                  </label>
                  <div className={`flex flex-col gap-1.5 ${isNested ? 'pl-0.5' : ''}`}>
                    {field.questions.map((q, idx) => {
                      const isAsked = askedSet.has(idx);
                      return (
                        <div
                          key={idx}
                          draggable={!readOnly}
                          onDragStart={readOnly ? undefined : (e) => {
                            e.dataTransfer.setData(PROBING_Q_MIME, q);
                            e.dataTransfer.setData('text/plain', q);
                            e.dataTransfer.effectAllowed = 'copy';
                          }}
                          onClick={() => toggleAsked(idx)}
                          className={`
                            px-2 py-1.5 rounded-lg text-xs font-medium italic leading-relaxed
                            ${readOnly ? 'cursor-default' : 'cursor-pointer'} select-none transition-all duration-200 border
                            ${isAsked
                              ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : readOnly
                                ? 'bg-white text-gray-800 border-gray-100'
                                : 'bg-white text-gray-800 border-gray-100 hover:bg-blue-50/60 hover:border-blue-100'}
                          `}
                          title={readOnly ? undefined : (isAsked ? 'Click to unmark — or drag into notes' : 'Click to mark as asked — or drag into notes')}
                        >
                          {/* items-start keeps the step number on the first line when copy wraps */}
                          <div className="flex items-start gap-2">
                            <span className="text-gray-400 not-italic font-semibold flex-shrink-0 min-w-[1.25rem] text-right">{idx + 1}.</span>
                            <span className="flex-1">{q}</span>
                            {isAsked && (
                              <span className="text-[10px] font-semibold text-blue-500 not-italic flex-shrink-0 whitespace-nowrap">
                                Marked as asked
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            </div>
          ) : !task.externalResources?.length && (
            <div className="flex items-center gap-2 py-3 px-4 bg-gray-50 rounded-lg border border-dashed border-gray-200">
              <svg className="w-5 h-5 text-gray-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-sm text-gray-400 italic">Helpful information to be displayed here</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  /**
   * Master Notes — a single StructuredNotesEditor where the clinician captures
   * all observations in real time during the task. When Criterion Mapping mode
   * is active, the editor becomes read-only (no new notes can be added) but
   * existing notes remain draggable into criterion rows.
   */
  const renderMasterNotes = ({ title = 'Observation Notes', className = 'mb-6' } = {}) => {
    const masterNotes = currentValue('masterNotes') || [];
    // Count only master notes that appear in at least one criterion — avoids
    // over-counting if a criterion somehow holds an orphaned note id.
    const mappedCount = masterNotes.filter((n) => mappedNoteIds.has(n.id)).length;
    const totalNotes = masterNotes.length;
    const allMapped = totalNotes > 0 && mappedCount === totalNotes;

    return (
      <div className={className}>
        {renderSectionHeading(
          criterionMappingMode ? 'Observation Notes (drag into criteria below)' : title,
          criterionMappingMode
            ? 'Drag note cards into the criterion rows below to map them.'
            : 'Capture all observations here. Use Tag on a note to link it to one or more DSM criteria for Criterion Mapping.'
        )}
        <div className={`bg-white rounded-xl border shadow-sm p-4 transition-colors duration-300 ${criterionMappingMode ? 'border-primary/30 bg-primary/[0.02]' : 'border-gray-200'}`}>
          {/*
            In Criterion Mapping the inbox stays read-only (no new notes, no
            edit/delete) so clinicians map what was captured in Task Admin —
            but grips must stay draggable so notes can be copied into DSM rows.
            `allowDragWhileReadOnly` is only set when the panel itself is
            editable: a "Not administered" / preview lock still disables drag.
          */}
          <StructuredNotesEditor
            notes={masterNotes}
            onChange={(criterionMappingMode || readOnly) ? undefined : (val) => onFieldChange('masterNotes', val)}
            placeholder={criterionMappingMode ? '' : 'Type observations here — press Enter to save each note as a card. Use Tag to link a note to DSM criteria…'}
            readOnlyPlaceholder={readOnly ? 'No observation notes recorded' : undefined}
            minHeight={criterionMappingMode ? '6rem' : '12rem'}
            domainId="master"
            readOnly={criterionMappingMode || readOnly}
            allowDragWhileReadOnly={criterionMappingMode && !readOnly}
            mappedNoteIds={criterionMappingMode ? mappedNoteIds : null}
            moveTargets={moveTargets}
            currentMoveId={`task:${task.id}:masterNotes`}
            onMoveNoteTo={(criterionMappingMode || readOnly) ? undefined : onMoveNote}
            // Task Administration is where clinicians capture live notes — Tag
            // lets them classify evidence against DSM criteria immediately,
            // so Criterion Mapping no longer needs a per-task drag-and-drop step.
            // v2-only affordance (taggingEnabled) — v1 patients keep plain notes.
            enableCriteriaTagging={taggingEnabled && !criterionMappingMode && !readOnly}
          />
          {/* Progress while mapping; green check when every note is assigned. */}
          {criterionMappingMode && totalNotes > 0 && (
            <p
              className={`text-xs mt-2 font-medium flex items-center gap-1.5 ${
                allMapped ? 'text-emerald-600' : 'text-gray-500'
              }`}
            >
              {allMapped && (
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              )}
              {allMapped
                ? `All notes mapped to criteria (${mappedCount}/${totalNotes})`
                : `${mappedCount}/${totalNotes} notes mapped to criteria`}
            </p>
          )}
        </div>
      </div>
    );
  };

  /**
   * DSM Criteria Contribution table — only visible when criterionMappingMode is ON.
   * Each criterion row accepts note drops from the master box.
   */
  const renderCriterionMapping = () => {
    if (!criterionMappingMode) return null;

    const extraCriteria = taskData?.dsm_extraCriteria || [];
    const activeCriteriaIds = [
      ...(task.relevantCriteria || []),
      ...extraCriteria.filter(id => !(task.relevantCriteria || []).includes(id)),
    ];
    const activeDomains = activeCriteriaIds
      .map(id => DSM5_DOMAINS.find(d => d.id === id))
      .filter(Boolean);
    const remainingDomains = DSM5_DOMAINS.filter(d => !activeCriteriaIds.includes(d.id));

    const handleAddCriterion = (criterionId) => {
      if (!criterionId) return;
      onFieldChange('dsm_extraCriteria', [...extraCriteria, criterionId]);
    };

    const handleRemoveExtraCriterion = (criterionId) => {
      setRemovingCriterionId(criterionId);
      setTimeout(() => {
        onFieldChange('dsm_extraCriteria', extraCriteria.filter(id => id !== criterionId));
        onFieldChange(`dsm_${criterionId}_evidence`, null);
        onFieldChange(`dsm_${criterionId}_notes`, []);
        setRemovingCriterionId(null);
      }, 200);
    };

    const rowColor = (val) => {
      switch (val) {
        case 'No evidence':      return 'bg-emerald-50';
        case 'Partial evidence': return 'bg-amber-50';
        case 'Clear evidence':   return 'bg-red-50';
        case 'Inconclusive':     return 'bg-gray-100';
        default:                 return 'bg-gray-50';
      }
    };

    return (
      <div className="mb-6">
        {renderSectionHeading(
          'Criterion Mapping',
          'Drag notes from the Observation Notes box above into the relevant criterion row. Set evidence thresholds when ready.'
        )}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-primary text-white">
                <th className="px-4 py-3 text-sm font-bold text-left" style={{ width: '30%' }}>DSM Criteria</th>
                <th className="px-4 py-3 text-sm font-bold text-left" style={{ width: '140px' }}>Threshold met</th>
                <th className="px-4 py-3 text-sm font-bold text-left">Mapped Notes</th>
                <th className="w-10" />
              </tr>
            </thead>

            <tbody>
              {activeDomains.map((domain) => {
                const isExtra = extraCriteria.includes(domain.id);
                const evidenceValue = currentValue(`dsm_${domain.id}_evidence`) || '';
                const notesValue = currentValue(`dsm_${domain.id}_notes`) || [];
                const isRemoving = removingCriterionId === domain.id;

                return (
                  <tr
                    key={domain.id}
                    className={`border-b border-gray-200 last:border-b-0 transition-colors duration-300 ${rowColor(evidenceValue)}`}
                    style={
                      isRemoving
                        ? { opacity: 0, maxHeight: 0, transition: 'all 200ms ease-in' }
                        : { animation: 'criterionSlideIn 200ms ease-out' }
                    }
                  >
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-start gap-1.5">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 font-bold leading-snug">
                            {domain.id}. {domain.shortTitle}
                          </p>
                          <p className="text-xs text-gray-500 leading-snug mt-0.5">
                            {domain.title}
                          </p>
                        </div>
                        <InfoTooltip
                          content={domain.tooltip}
                          label={`${domain.id} criteria detail`}
                          maxWidth="300px"
                        />
                      </div>
                    </td>

                    <td className="px-4 py-3 align-top">
                      <div className="flex flex-col gap-1.5">
                        {DSM5_EVIDENCE_OPTIONS.map(opt => {
                          const isSelected = evidenceValue === opt;
                          const colours = EVIDENCE_BUTTON_COLOURS[opt] || EVIDENCE_BUTTON_COLOURS['Inconclusive'];
                          return (
                            <button
                              key={opt}
                              onClick={readOnly ? undefined : () => {
                                onFieldChange(`dsm_${domain.id}_evidence`, isSelected ? null : opt);
                              }}
                              disabled={readOnly}
                              className={`
                                px-2.5 py-1 rounded-lg text-xs font-semibold border
                                transition-all duration-150 whitespace-nowrap
                                ${readOnly ? 'cursor-default' : ''}
                                ${isSelected
                                  ? `${colours.selectedBg} ${colours.selectedBorder} text-white shadow-sm`
                                  : `bg-white text-gray-600 border-gray-300 ${readOnly ? '' : colours.hoverBorder}`
                                }
                              `}
                              aria-pressed={isSelected}
                              aria-label={`${opt} for ${domain.id}`}
                            >
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    </td>

                    <td className="px-4 py-3 align-top">
                      <StructuredNotesEditor
                        notes={notesValue}
                        onChange={readOnly ? undefined : (val) => onFieldChange(`dsm_${domain.id}_notes`, val)}
                        placeholder={`Drop or type notes for ${domain.id}...`}
                        readOnlyPlaceholder="No notes mapped"
                        minHeight="5rem"
                        domainId={domain.id}
                        onMoveNoteIn={readOnly ? undefined : handleMoveNoteBetweenCriteria}
                        readOnly={readOnly}
                      />
                    </td>

                    <td className="px-2 py-3 align-top">
                      {isExtra && !readOnly ? (
                        <button
                          onClick={() => handleRemoveExtraCriterion(domain.id)}
                          className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                          title="Remove this criterion"
                          aria-label={`Remove ${domain.id}`}
                        >
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      ) : (
                        <span className="block w-4" />
                      )}
                    </td>
                  </tr>
                );
              })}

              {remainingDomains.length > 0 && !readOnly && (
                <tr className="border-t border-dashed border-gray-300">
                  <td colSpan={4} className="px-4 py-2.5">
                    {!addSymptomPickerOpen ? (
                      <button
                        onClick={() => setAddSymptomPickerOpen(true)}
                        className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-primary transition-colors duration-150"
                      >
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-gray-100 text-gray-400 text-xs font-bold flex-shrink-0">+</span>
                        Add DSM Criteria
                      </button>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2" style={{ animation: 'criterionSlideIn 200ms ease-out' }}>
                        <span className="text-xs text-gray-500 font-medium mr-1">Add:</span>
                        {remainingDomains.map(d => (
                          <button
                            key={d.id}
                            onClick={() => {
                              handleAddCriterion(d.id);
                              setAddSymptomPickerOpen(false);
                            }}
                            className="px-2.5 py-1 rounded-lg text-xs font-semibold border border-gray-300 bg-white text-gray-600 hover:border-primary hover:text-primary hover:bg-primary/5 transition-all duration-150"
                          >
                            {d.id}. {d.shortTitle}
                          </button>
                        ))}
                        <button
                          onClick={() => setAddSymptomPickerOpen(false)}
                          className="ml-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                          title="Cancel"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // -------------------------------------------------------------------
  // Main render
  // -------------------------------------------------------------------

  return (
    <div className="flex-grow p-6 text-left">
      <style>{`
        @keyframes criterionSlideIn {
          from { opacity: 0; transform: translateY(-8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Task header */}
      <div className="mb-6 text-left">
        <div className="flex items-center gap-3">
          <h1 className="text-gray-900 text-3xl font-bold">
            Task {selectedTaskIds.indexOf(task.id) + 1}. {task.shortTitle}
          </h1>
          {criterionMappingMode && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-200">
              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              Criterion Mapping
            </span>
          )}

          {/* "Not administered" control — interactive switch on Task
              Administration, read-only pill elsewhere. Pushed to the right. */}
          {canToggleNotAdministered ? (
            <label className="flex items-center gap-2 cursor-pointer select-none ml-auto">
              <span className="text-xs font-medium text-gray-500 whitespace-nowrap">Not administered</span>
              <button
                type="button"
                role="switch"
                aria-checked={notAdministered}
                aria-label="Mark this task as not administered"
                onClick={() => onToggleNotAdministered(!notAdministered)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:ring-offset-1 ${notAdministered ? 'bg-gray-500' : 'bg-gray-300'}`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transform transition-transform duration-200 ${notAdministered ? 'translate-x-[18px]' : 'translate-x-[3px]'}`}
                />
              </button>
            </label>
          ) : notAdministered ? (
            <span className="ml-auto inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 border border-gray-300">
              Not administered
            </span>
          ) : null}
        </div>
        <p className="text-gray-500 text-sm mt-1">
          <span className="font-semibold">Focus of observation: </span>
          {task.focusOfObservation || task.fullTitle}
        </p>
      </div>

      {/* Explanatory banner when the task is excluded from the assessment. */}
      {notAdministered && (
        <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-left">
          <p className="text-sm text-gray-600">
            <span className="font-semibold text-gray-700">This task was not administered.</span>{' '}
            Any notes below are kept for reference but are read-only and are excluded
            from the criteria summary and generated report.
            {canToggleNotAdministered
              ? ' Switch off "Not administered" above to edit this task again.'
              : ''}
          </p>
        </div>
      )}

      {/* Greyed while not administered — content stays visible but muted. */}
      <div className={notAdministered ? 'opacity-60' : ''}>

      {/* Section A — Task Administration: hidden with smooth animation when
          criterion mapping mode is active (clinician is in post-session review).
          On the Task Administration page, Section A and master notes render
          side-by-side so clinicians can reference questions while typing notes. */}
      {sideBySideLayout && !criterionMappingMode ? (
        <div className="flex gap-6 items-start">
          <div className="flex-1 min-w-0">
            {renderQuestionnaireSection()}

            {showObservations && task.sections.filter(s => s.id === 'B').map((section) => (
              <div key={section.id} className="mb-6">
                <button
                  type="button"
                  onClick={() => setSectionBOpen(prev => !prev)}
                  className="flex items-center gap-2 mb-3 group cursor-pointer"
                >
                  <h2 className="text-gray-900 text-base font-semibold text-left">{section.title}</h2>
                  <ChevronDownIcon
                    className={`w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-transform duration-200 ${sectionBOpen ? 'rotate-0' : '-rotate-90'}`}
                  />
                </button>
                <div
                  className="overflow-hidden transition-all duration-300 ease-in-out"
                  style={{
                    display: 'grid',
                    gridTemplateRows: sectionBOpen ? '1fr' : '0fr',
                    opacity: sectionBOpen ? 1 : 0,
                  }}
                >
                  <div className="min-h-0">
                    <div className="px-5 py-4 bg-white rounded-xl border border-gray-100 space-y-5">
                      {section.subsections?.map((sub, idx) => (
                        <div key={idx}>
                          <h3 className="text-sm font-semibold text-gray-800 mb-2 text-left">{sub.title}</h3>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex-1 min-w-0 sticky top-0 self-start">
            {renderMasterNotes({ title: 'General Observation Notes', className: '' })}
          </div>
        </div>
      ) : (
      <div
        className="overflow-hidden transition-all ease-in-out"
        style={{
          display: 'grid',
          gridTemplateRows: criterionMappingMode ? '0fr' : '1fr',
          opacity: criterionMappingMode ? 0 : 1,
          transitionDuration: '400ms',
        }}
      >
        <div className="min-h-0">
          {renderQuestionnaireSection()}

          {/* Section B — Observations (collapsible behavioural checkboxes) */}
          {showObservations && task.sections.filter(s => s.id === 'B').map((section) => (
            <div key={section.id} className="mb-6">
              <button
                type="button"
                onClick={() => setSectionBOpen(prev => !prev)}
                className="flex items-center gap-2 mb-3 group cursor-pointer"
              >
                <h2 className="text-gray-900 text-base font-semibold text-left">{section.title}</h2>
                <ChevronDownIcon
                  className={`w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-transform duration-200 ${sectionBOpen ? 'rotate-0' : '-rotate-90'}`}
                />
              </button>
              <div
                className="overflow-hidden transition-all duration-300 ease-in-out"
                style={{
                  display: 'grid',
                  gridTemplateRows: sectionBOpen ? '1fr' : '0fr',
                  opacity: sectionBOpen ? 1 : 0,
                }}
              >
                <div className="min-h-0">
                  <div className="px-5 py-4 bg-white rounded-xl border border-gray-100 space-y-5">
                    {section.subsections?.map((sub, idx) => (
                      <div key={idx}>
                        <h3 className="text-sm font-semibold text-gray-800 mb-2 text-left">{sub.title}</h3>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Master Notes — stacked below when not in side-by-side Task Admin layout */}
      {!(sideBySideLayout && !criterionMappingMode) && renderMasterNotes()}

      {/* Criterion Mapping — only visible when toggle is ON */}
      {renderCriterionMapping()}
      </div>
    </div>
  );
}
